import React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';
import { FormControl } from "@common/components/form-control";

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Loading } from "@common/components/loading";
import { HttpErrorDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import Tree, { TreeGetIdArgs } from "@common/components/Tree";

import "./copy-pedigree.styles.scss";

export type CopyPedigreeProps = {
  onClose: () => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  showErrorMessage: (error: string) => void;
  onSubmit: (folder: FolderPrimaryListDto, pedigreeName: string) => Promise<void>;
};

export const CopyPedigree: React.FC<CopyPedigreeProps> = (props) => {
  const { onClose, onSubmit, showErrorMessage, getFolders } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [initializing, setInitializingState] = React.useState(true);
  const [pedigreeName, setPedigreeName] = React.useState('');
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();

  React.useEffect(() => {
    setInitializingState(true);
    getFolders()
      .then((data) => {
        setFolders(data);
      })
      .catch((err) => {
        showErrorMessage(err?.entity || I18n.t("Failed to get folders"));
        console.error(err);
      })
      .finally(() => {
        setInitializingState(false);
      });
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    
    setSubmitting(true);

    try {
      await onSubmit(selectedFolder, pedigreeName);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to copy pedigree"));
    }
  }, [onSubmit, showErrorMessage, selectedFolder, pedigreeName]);

  const handleFolderChange = React.useCallback((id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    if (id === selectedFolder?.id) {
      return;
    }

    setSelectedFolder(folder);
  }, [selectedFolder]);

  const handlePedigreeNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPedigreeName(e.target.value);
  }, []);

  const getFolderId = React.useMemo(() => {
    return {
      folder: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}`,
      collapseIcon: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}-image`,
    };
  }, []);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="PedigreesCopyPedigree"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-ped-sec-close' })}
        >
          {I18n.t("Copy Pedigree")}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializing 
          ? <Flex className="PedigreesCopyPedigree__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : (
            <>
              <Flex alignItems="center">
                <span>{I18n.t("Pedigree Name")}</span>
                <FormControl
                  className="PedigreesCopyPedigree__name-input"
                  value={pedigreeName}
                  {...getTestAttributes({ id: 'tx-copy-ped-name' })}
                  onChange={handlePedigreeNameChange}
                />
                <Button
                  disabled={submitting || !selectedFolder || !pedigreeName || initializing}
                  {...getTestAttributes({ id: 'btn-copy-ped-save' })}
                  onClick={handleSubmit}
                >
                  {I18n.t("Copy")}
                </Button>
              </Flex>
              <Tree 
                getId={{
                  folder: getFolderId.folder,
                  collapseIcon: getFolderId.collapseIcon,
                }}
                className="PedigreesCopyPedigree__folders"
                items={folders}
                value={selectedFolder ? selectedFolder.id : undefined} 
                onChange={handleFolderChange} 
              />
            </>
          )
        }
      </ModalBody>
    </Modal>
  );
};
